import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import axios from "axios";
import {
  FileText, Mail, Sparkles, ArrowRight, CheckCircle2,
  Clock, Upload, ListChecks, ChevronRight, Star, Shield,
  AlertTriangle, Zap, Users, CalendarClock, XCircle
} from "lucide-react";
import { useAuth, API } from "../App";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_contract-flow-21/artifacts/cuiv7qxm_eo-logo-blue-stacked-header-logo.png";
const HERO_BG = "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=1920&q=80";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.1, duration: 0.6, ease: [0.22, 1, 0.36, 1] }
  })
};

const Landing = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth() || {};
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const utm_source = searchParams.get("utm_source");
    const utm_medium = searchParams.get("utm_medium");
    const utm_campaign = searchParams.get("utm_campaign");
    const utm_term = searchParams.get("utm_term");
    const utm_content = searchParams.get("utm_content");
    if (utm_source || utm_medium || utm_campaign) {
      axios.post(`${API}/analytics/track`, {
        utm_source, utm_medium, utm_campaign, utm_term, utm_content,
        referrer: document.referrer || null,
        page: window.location.pathname,
      }).catch(() => {});
    }
  }, [searchParams]);

  const handleCTA = () => {
    if (user) {
      navigate("/dashboard");
    } else {
      const redirectUrl = window.location.origin + '/dashboard';
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "#F9F6F0", fontFamily: "'Figtree', sans-serif" }}>
      {/* ─── NAVBAR ─── */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "backdrop-blur-xl bg-[#F9F6F0]/85 border-b border-[#2C4C3B]/10 shadow-sm" : "bg-transparent"
        }`}
        data-testid="landing-navbar"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-10 flex items-center justify-between h-16 md:h-20">
          <img src={LOGO_URL} alt="EasyOffice" className="h-9 md:h-11 w-auto" data-testid="landing-logo" />
          <div className="hidden md:flex items-center gap-10 text-sm font-medium text-[#2C4C3B]/70">
            <a href="#features" className="hover:text-[#2C4C3B] transition-colors">Features</a>
            <a href="#how-it-works" className="hover:text-[#2C4C3B] transition-colors">How It Works</a>
            <a href="#testimonials" className="hover:text-[#2C4C3B] transition-colors">Results</a>
          </div>
          <button
            onClick={handleCTA}
            className="px-5 py-2.5 rounded-full text-sm font-semibold text-white transition-all hover:scale-105 active:scale-100"
            style={{ background: "#CC7753" }}
            data-testid="landing-nav-cta"
          >
            Start Free — 30 Seconds
          </button>
        </div>
      </nav>

      {/* ─── HERO ─── */}
      <section className="relative min-h-[92vh] flex items-end" data-testid="landing-hero">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="Luxury home" className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#2C4C3B]/95 via-[#2C4C3B]/60 to-[#2C4C3B]/10" />
        </div>
        <div className="relative w-full max-w-7xl mx-auto px-5 md:px-10 pb-16 md:pb-24 pt-32">
          <motion.div initial="hidden" animate="visible" className="max-w-3xl">
            <motion.p
              variants={fadeUp}
              custom={0}
              className="text-xs md:text-sm uppercase tracking-[0.25em] text-[#CC7753] font-semibold mb-5"
            >
              Built for Transaction Coordinators & Real Estate Teams
            </motion.p>
            <motion.h1
              variants={fadeUp}
              custom={1}
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white leading-[1.08] tracking-tight mb-6"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            >
              Stop chasing deadlines.<br />
              <span className="text-[#CC7753]">Start closing deals.</span>
            </motion.h1>
            <motion.p
              variants={fadeUp}
              custom={2}
              className="text-base md:text-xl text-white/80 leading-relaxed max-w-xl mb-8"
            >
              The transaction management platform that replaces your spreadsheets, automates your emails, and makes sure nothing falls through the cracks — from contract to close.
            </motion.p>
            <motion.div variants={fadeUp} custom={3} className="flex flex-col sm:flex-row items-start gap-4">
              <button
                onClick={handleCTA}
                className="group px-8 py-4 rounded-full text-base font-semibold text-white flex items-center gap-2.5 transition-all hover:scale-105 active:scale-100 shadow-lg shadow-[#CC7753]/25"
                style={{ background: "#CC7753" }}
                data-testid="hero-cta-button"
              >
                Create Your Free Account
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </button>
              <div className="text-white/50 text-sm pt-2 space-y-0.5">
                <p className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[#CC7753]" /> Sign up with Google — takes 30 seconds</p>
                <p className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[#CC7753]" /> Free forever plan. No credit card.</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ─── PAIN STRIP ─── */}
      <section className="py-10 md:py-14 px-5 md:px-10 bg-white border-b border-[#2C4C3B]/10" data-testid="landing-pain-strip">
        <div className="max-w-7xl mx-auto">
          <motion.p
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-center text-sm md:text-base font-semibold text-[#2C4C3B]/50 uppercase tracking-wider mb-8"
          >
            Sound familiar?
          </motion.p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                pain: "Deadlines buried in spreadsheets",
                fix: "Every deadline auto-calculated from your contract dates",
                icon: AlertTriangle,
                color: "#dc2626",
              },
              {
                pain: "Manually emailing agents, lenders, title",
                fix: "Automated emails fire on schedule with the right details",
                icon: XCircle,
                color: "#dc2626",
              },
              {
                pain: "Hours spent entering contract data by hand",
                fix: "AI reads your PDF and populates every field in seconds",
                icon: Clock,
                color: "#dc2626",
              },
            ].map((item, i) => (
              <motion.div
                key={item.pain}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
                className="relative bg-[#F9F6F0] rounded-2xl p-6 border border-[#2C4C3B]/8"
              >
                <div className="flex items-start gap-3 mb-4">
                  <item.icon className="w-5 h-5 text-red-500/70 mt-0.5 shrink-0" />
                  <p className="text-sm text-[#2C4C3B]/50 line-through decoration-red-400/40">{item.pain}</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#2C4C3B] mt-0.5 shrink-0" />
                  <p className="text-sm font-semibold text-[#2C4C3B]">{item.fix}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SOCIAL PROOF MARQUEE ─── */}
      <section className="py-5 border-b border-[#2C4C3B]/10" data-testid="landing-social-proof">
        <Marquee speed={35} gradient gradientColor="#F9F6F0" gradientWidth={80}>
          {[
            "Used by Teams Doing 200+ Closings/Year",
            "AI Contract Reader Saves 4+ Hours Per Deal",
            "Zero Missed Deadlines",
            "Set Up in Under 2 Minutes",
            "Automated Email Workflows",
            "Trusted by Top-Producing Agents & TCs",
          ].map((text) => (
            <span key={text} className="flex items-center gap-3 mx-10 text-sm font-medium text-[#2C4C3B]/50">
              <Star className="w-3 h-3 fill-[#CC7753] text-[#CC7753]" />
              {text}
            </span>
          ))}
        </Marquee>
      </section>

      {/* ─── FEATURES BENTO ─── */}
      <section id="features" className="py-20 md:py-32 px-5 md:px-10" data-testid="landing-features">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            className="text-center mb-14 md:mb-20"
          >
            <motion.p variants={fadeUp} custom={0} className="text-xs md:text-sm uppercase tracking-[0.2em] text-[#CC7753] font-semibold mb-3">
              Why Teams Switch to EasyOffice
            </motion.p>
            <motion.h2
              variants={fadeUp}
              custom={1}
              className="text-3xl sm:text-4xl md:text-5xl text-[#2C4C3B] tracking-tight"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            >
              Everything you need, nothing you don't
            </motion.h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6">
            {/* AI Contract Reading */}
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }}
              variants={fadeUp} custom={0}
              className="md:col-span-8 bg-white rounded-2xl border border-[#2C4C3B]/10 p-8 md:p-12 flex flex-col justify-between min-h-[320px] group hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
              data-testid="feature-ai-contract"
            >
              <div>
                <div className="w-12 h-12 rounded-xl bg-[#CC7753]/10 flex items-center justify-center mb-6">
                  <Sparkles className="w-6 h-6 text-[#CC7753]" />
                </div>
                <h3 className="text-2xl md:text-3xl text-[#2C4C3B] mb-4" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
                  AI Contract Reader
                </h3>
                <p className="text-[#2C4C3B]/70 text-base md:text-lg leading-relaxed max-w-lg">
                  Upload a contract PDF and watch AI extract every critical date, price, and party — closing date, earnest money, inspection deadlines — all populated automatically in seconds.
                </p>
              </div>
              <div className="flex items-center gap-3 mt-8 pt-6 border-t border-[#2C4C3B]/5">
                <div className="flex items-center gap-5 text-sm text-[#2C4C3B]/60">
                  <span className="flex items-center gap-1.5"><Upload className="w-4 h-4" /> Drop a PDF</span>
                  <span className="flex items-center gap-1.5"><FileText className="w-4 h-4" /> Auto-extract</span>
                  <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Review & confirm</span>
                </div>
              </div>
            </motion.div>

            {/* Email Reminders */}
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }}
              variants={fadeUp} custom={1}
              className="md:col-span-4 bg-[#2C4C3B] rounded-2xl p-8 md:p-10 flex flex-col justify-between min-h-[320px] group hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
              data-testid="feature-email-reminders"
            >
              <div>
                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mb-6">
                  <Mail className="w-6 h-6 text-[#CC7753]" />
                </div>
                <h3 className="text-2xl text-white mb-3" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
                  Automated Emails & Reminders
                </h3>
                <p className="text-white/60 text-base leading-relaxed">
                  Pre-built email templates with dynamic variables. Notify agents, lenders, title companies, and clients — automatically, on time, every time.
                </p>
              </div>
              <div className="flex items-center gap-2 mt-6 text-[#CC7753] text-sm font-medium">
                <span>Fully customizable</span>
                <ChevronRight className="w-4 h-4" />
              </div>
            </motion.div>

            {/* Workflow Templates */}
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }}
              variants={fadeUp} custom={2}
              className="md:col-span-6 bg-white rounded-2xl border border-[#2C4C3B]/10 p-8 md:p-10 flex flex-col justify-between min-h-[260px] group hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
              data-testid="feature-workflows"
            >
              <div>
                <div className="w-12 h-12 rounded-xl bg-[#CC7753]/10 flex items-center justify-center mb-6">
                  <ListChecks className="w-6 h-6 text-[#CC7753]" />
                </div>
                <h3 className="text-2xl text-[#2C4C3B] mb-3" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
                  Customizable Workflows
                </h3>
                <p className="text-[#2C4C3B]/70 text-base leading-relaxed">
                  Build reusable checklists with task groups, dependent deadlines, and role assignments. Import your existing workflows instantly.
                </p>
              </div>
            </motion.div>

            {/* Dynamic Deadlines */}
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }}
              variants={fadeUp} custom={3}
              className="md:col-span-6 bg-[#F9F6F0] rounded-2xl border border-[#2C4C3B]/10 p-8 md:p-10 flex flex-col justify-between min-h-[260px] group hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
              data-testid="feature-deadlines"
            >
              <div>
                <div className="w-12 h-12 rounded-xl bg-[#2C4C3B]/10 flex items-center justify-center mb-6">
                  <Clock className="w-6 h-6 text-[#2C4C3B]" />
                </div>
                <h3 className="text-2xl text-[#2C4C3B] mb-3" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
                  Dynamic Deadline Tracking
                </h3>
                <p className="text-[#2C4C3B]/70 text-base leading-relaxed">
                  Closing date changed? All dependent deadlines shift automatically. Everyone gets notified. No spreadsheets, no panic.
                </p>
              </div>
            </motion.div>
          </div>

          {/* Mid-page CTA */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={fadeUp}
            className="mt-14 text-center"
          >
            <button
              onClick={handleCTA}
              className="group px-8 py-4 rounded-full text-base font-semibold text-white inline-flex items-center gap-2.5 transition-all hover:scale-105 active:scale-100 shadow-lg shadow-[#CC7753]/20"
              style={{ background: "#CC7753" }}
              data-testid="mid-cta-button"
            >
              Try It Free — Set Up in 2 Minutes
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </button>
            <p className="text-[#2C4C3B]/40 text-sm mt-3">No credit card. No contracts. Cancel anytime.</p>
          </motion.div>
        </div>
      </section>

      {/* ─── HOW IT WORKS ─── */}
      <section id="how-it-works" className="py-20 md:py-32 px-5 md:px-10 bg-white" data-testid="landing-how-it-works">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}
            className="text-center mb-16 md:mb-24"
          >
            <motion.p variants={fadeUp} custom={0} className="text-xs md:text-sm uppercase tracking-[0.2em] text-[#CC7753] font-semibold mb-3">
              You're 3 steps from organized
            </motion.p>
            <motion.h2
              variants={fadeUp} custom={1}
              className="text-3xl sm:text-4xl md:text-5xl text-[#2C4C3B] tracking-tight"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            >
              Up and running before your coffee gets cold
            </motion.h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {[
              {
                num: "01",
                title: "Sign Up with Google",
                desc: "One click. No passwords to remember, no forms to fill out. Your account is ready in 30 seconds flat.",
                icon: Zap,
              },
              {
                num: "02",
                title: "Upload Your First Contract",
                desc: "Drop a PDF — AI extracts every date, name, and dollar amount. Review it, tweak anything, and you're set.",
                icon: Upload,
              },
              {
                num: "03",
                title: "Apply a Workflow & Go",
                desc: "Pick a pre-built template or build your own. Tasks, emails, and reminders handle themselves. You just close.",
                icon: ListChecks,
              },
            ].map((step, i) => (
              <motion.div
                key={step.num}
                initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }}
                variants={fadeUp} custom={i}
                className="relative"
                data-testid={`how-step-${i}`}
              >
                <div className="flex items-center gap-4 mb-5">
                  <span
                    className="text-6xl md:text-7xl font-bold text-[#CC7753]/10 leading-none"
                    style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                  >
                    {step.num}
                  </span>
                  <div className="w-11 h-11 rounded-xl bg-[#CC7753]/10 flex items-center justify-center">
                    <step.icon className="w-5 h-5 text-[#CC7753]" />
                  </div>
                </div>
                <h3 className="text-xl md:text-2xl text-[#2C4C3B] mb-3" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
                  {step.title}
                </h3>
                <p className="text-[#2C4C3B]/70 text-base leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── TESTIMONIALS ─── */}
      <section id="testimonials" className="py-20 md:py-32 px-5 md:px-10" data-testid="landing-testimonials">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}
            className="text-center mb-14"
          >
            <motion.p variants={fadeUp} custom={0} className="text-xs md:text-sm uppercase tracking-[0.2em] text-[#CC7753] font-semibold mb-3">
              Real Results
            </motion.p>
            <motion.h2
              variants={fadeUp} custom={1}
              className="text-3xl sm:text-4xl md:text-5xl text-[#2C4C3B] tracking-tight"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            >
              Teams that switched never looked back
            </motion.h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }}
              variants={fadeUp}
              className="bg-white rounded-2xl border border-[#2C4C3B]/10 p-8 md:p-12"
            >
              <div className="flex gap-1 mb-6">
                {[...Array(5)].map((_, i) => (<Star key={`s1-${i}`} className="w-5 h-5 fill-[#CC7753] text-[#CC7753]" />))}
              </div>
              <blockquote className="text-xl md:text-2xl text-[#2C4C3B] leading-relaxed mb-8" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
                "We went from juggling spreadsheets to having every transaction on autopilot. The AI contract reader alone saves us hours every week."
              </blockquote>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#2C4C3B] flex items-center justify-center text-white font-semibold text-sm">SK</div>
                <div>
                  <p className="font-semibold text-[#2C4C3B]">Sarah K.</p>
                  <p className="text-sm text-[#2C4C3B]/60">Transaction Coordinator, Top 1% Team</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }}
              variants={fadeUp} custom={1}
              className="bg-white rounded-2xl border border-[#2C4C3B]/10 p-8 md:p-12"
            >
              <div className="flex gap-1 mb-6">
                {[...Array(5)].map((_, i) => (<Star key={`s2-${i}`} className="w-5 h-5 fill-[#CC7753] text-[#CC7753]" />))}
              </div>
              <blockquote className="text-xl md:text-2xl text-[#2C4C3B] leading-relaxed mb-8" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
                "Finally, a tool that thinks like a real estate professional. The automated emails and deadline tracking give me peace of mind on every single deal."
              </blockquote>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#CC7753] flex items-center justify-center text-white font-semibold text-sm">MR</div>
                <div>
                  <p className="font-semibold text-[#2C4C3B]">Michael R.</p>
                  <p className="text-sm text-[#2C4C3B]/60">Broker, 200+ Closings/Year</p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Post-testimonial CTA */}
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }}
            variants={fadeUp}
            className="mt-14 text-center"
          >
            <button
              onClick={handleCTA}
              className="group px-8 py-4 rounded-full text-base font-semibold text-white inline-flex items-center gap-2.5 transition-all hover:scale-105 active:scale-100 shadow-lg shadow-[#CC7753]/20"
              style={{ background: "#CC7753" }}
              data-testid="post-testimonial-cta"
            >
              See It For Yourself — Free
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* ─── TRUST BAR ─── */}
      <section className="py-12 px-5 md:px-10 bg-white border-y border-[#2C4C3B]/10" data-testid="landing-trust-bar">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { val: "500+", label: "Transactions Managed" },
            { val: "98%", label: "On-Time Close Rate" },
            { val: "4hrs", label: "Saved Per Transaction" },
            { val: "0", label: "Missed Deadlines" },
          ].map((stat, i) => (
            <motion.div key={stat.label} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i}>
              <p className="text-3xl md:text-4xl font-bold text-[#2C4C3B] mb-1" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
                {stat.val}
              </p>
              <p className="text-sm text-[#2C4C3B]/50">{stat.label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ─── OBJECTION HANDLER ─── */}
      <section className="py-16 md:py-24 px-5 md:px-10" data-testid="landing-objections">
        <div className="max-w-4xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} className="text-center mb-12">
            <motion.h2
              variants={fadeUp}
              className="text-2xl sm:text-3xl md:text-4xl text-[#2C4C3B] tracking-tight"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            >
              "But I already have a system..."
            </motion.h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {[
              {
                q: "I'm using spreadsheets and it works fine.",
                a: "Until a closing date changes and you spend 45 minutes recalculating every deadline. EasyOffice shifts them all automatically.",
              },
              {
                q: "I don't have time to learn a new tool.",
                a: "Sign up with Google, upload a contract, done. Most users are managing their first transaction within 5 minutes.",
              },
              {
                q: "My team won't adopt another platform.",
                a: "There's nothing to learn. Assign tasks, and your team gets email reminders with exactly what they need to do and when.",
              },
              {
                q: "I've tried transaction management software before.",
                a: "We built this with active TCs and agents, not by guessing. If it doesn't save you time in the first week, just stop using it — it's free.",
              },
            ].map((item, i) => (
              <motion.div
                key={item.q}
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                variants={fadeUp} custom={i}
                className="bg-white rounded-xl p-6 border border-[#2C4C3B]/8"
              >
                <p className="text-sm font-semibold text-[#2C4C3B]/80 mb-2 italic">"{item.q}"</p>
                <p className="text-sm text-[#2C4C3B]/60 leading-relaxed">{item.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA ─── */}
      <section className="py-24 md:py-36 px-5 md:px-10 bg-[#2C4C3B]" data-testid="landing-final-cta">
        <motion.div
          initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}
          className="max-w-3xl mx-auto text-center"
        >
          <motion.div variants={fadeUp} custom={0} className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center mx-auto mb-8">
            <Shield className="w-7 h-7 text-[#CC7753]" />
          </motion.div>
          <motion.h2
            variants={fadeUp} custom={1}
            className="text-3xl sm:text-4xl md:text-5xl text-white tracking-tight mb-5"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          >
            Your next closing doesn't have to be stressful
          </motion.h2>
          <motion.p variants={fadeUp} custom={2} className="text-lg text-white/60 mb-4 max-w-lg mx-auto">
            Join the agents and TCs who stopped duct-taping their process together and started actually enjoying the close.
          </motion.p>
          <motion.div variants={fadeUp} custom={3} className="flex flex-col items-center gap-4">
            <button
              onClick={handleCTA}
              className="group px-10 py-4 rounded-full text-base font-semibold text-white flex items-center gap-2.5 transition-all hover:scale-105 active:scale-100 shadow-lg shadow-[#CC7753]/30"
              style={{ background: "#CC7753" }}
              data-testid="final-cta-button"
            >
              Create Your Free Account Now
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </button>
            <div className="flex flex-col items-center gap-1.5 text-sm text-white/40">
              <p className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-[#CC7753]" /> Free forever plan — no credit card</p>
              <p className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-[#CC7753]" /> Set up in 30 seconds with Google</p>
              <p className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-[#CC7753]" /> Cancel anytime — no strings attached</p>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="py-10 px-5 md:px-10 bg-[#2C4C3B] border-t border-white/10" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <img src={LOGO_URL} alt="EasyOffice" className="h-8 w-auto opacity-70" />
          <p className="text-sm text-white/30">
            EasyOffice Transaction Manager &copy; {new Date().getFullYear()}. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
